import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  CreateProviderContactChangeRequest,
  ProviderAccessRestoreContactState,
  ProviderContactChange,
  ProviderContactChangeChannel,
  SubmitRestoreContactsRequest,
} from '@models';
import { delay, Observable, of, throwError } from 'rxjs';
import { ProviderContactChangeService } from './provider-contact-change.service';

const MOCK_VALID_CODE = '123456';
const MOCK_TAKEN_EMAIL = 'taken@example.com';
const CODE_LIFETIME_MS = 10 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class ProviderContactChangeMockService
  implements ProviderContactChangeService
{
  private readonly changes = new Map<string, ProviderContactChange>();

  create(
    _patientId: string,
    request: CreateProviderContactChangeRequest
  ): Observable<ProviderContactChange> {
    if (request.type === 'Restore') {
      return this.createRestore(request);
    }

    const newValue = request.newValue;
    if (newValue.trim().toLowerCase() === MOCK_TAKEN_EMAIL) {
      return this.fail(409, 'CONTACT_CHANGE_EMAIL_ALREADY_REGISTERED');
    }

    const now = Date.now();
    const change: ProviderContactChange = {
      id: crypto.randomUUID(),
      type: request.type,
      status: 'PendingCurrentVerification',
      maskedApprovalDestination:
        request.type === 'Email' ? '+1 **** *** 1234' : 'j****@example.com',
      maskedNewValue: this.mask(request.type, newValue),
      expiresAtUtc: new Date(now + 30 * 60 * 1000).toISOString(),
      codeExpiresAtUtc: new Date(now + CODE_LIFETIME_MS).toISOString(),
      attemptsRemaining: 5,
      resendCooldownSeconds: 30,
      resendsRemaining: 5,
    };
    this.changes.set(change.id, change);
    return of(change).pipe(delay(300));
  }

  get(_patientId: string, id: string): Observable<ProviderContactChange> {
    const change = this.changes.get(id);
    return change
      ? of({ ...change, resendCooldownSeconds: 0 })
      : this.fail(404, 'CONTACT_CHANGE_NOT_FOUND');
  }

  approve(
    _patientId: string,
    id: string,
    code: string
  ): Observable<ProviderContactChange> {
    const change = this.changes.get(id);
    if (!change || change.status !== 'PendingCurrentVerification') {
      return this.fail(409, 'CONTACT_CHANGE_INVALID_STATE');
    }

    if (code !== MOCK_VALID_CODE) {
      return this.registerFailure(change);
    }

    if (change.type === 'Restore') {
      const confirmed: ProviderContactChange = {
        ...change,
        status: 'PendingNewValue',
        codeExpiresAtUtc: null,
        resendCooldownSeconds: 0,
      };
      this.changes.set(id, confirmed);
      return of(confirmed).pipe(delay(300));
    }

    const approved: ProviderContactChange = {
      ...change,
      status: 'PendingNewVerification',
      codeExpiresAtUtc: new Date(Date.now() + CODE_LIFETIME_MS).toISOString(),
      resendCooldownSeconds: 30,
    };
    this.changes.set(id, approved);
    return of(approved).pipe(delay(300));
  }

  submitContacts(
    _patientId: string,
    id: string,
    request: SubmitRestoreContactsRequest
  ): Observable<ProviderContactChange> {
    const change = this.changes.get(id);
    if (
      !change ||
      change.type !== 'Restore' ||
      (change.status !== 'PendingNewValue' &&
        change.status !== 'PendingNewVerification')
    ) {
      return this.fail(409, 'CONTACT_CHANGE_INVALID_STATE');
    }

    if (request.email.trim().toLowerCase() === MOCK_TAKEN_EMAIL) {
      return this.fail(409, 'CONTACT_CHANGE_EMAIL_ALREADY_REGISTERED');
    }

    const resubmitted = change.status === 'PendingNewVerification';
    const submitted: ProviderContactChange = {
      ...change,
      status: 'PendingNewVerification',
      maskedNewValue: this.mask('Email', request.email),
      resendsRemaining: resubmitted
        ? change.resendsRemaining - 1
        : change.resendsRemaining,
      restore: {
        identityMethod: change.restore?.identityMethod ?? null,
        email: this.sentContact(this.mask('Email', request.email)),
        phone: this.sentContact(this.mask('Phone', request.phone)),
      },
    };
    this.changes.set(id, submitted);
    return of(submitted).pipe(delay(300));
  }

  verify(
    _patientId: string,
    id: string,
    code: string,
    channel?: ProviderContactChangeChannel
  ): Observable<ProviderContactChange> {
    const change = this.changes.get(id);
    if (!change || change.status !== 'PendingNewVerification') {
      return this.fail(409, 'CONTACT_CHANGE_INVALID_STATE');
    }

    if (code !== MOCK_VALID_CODE) {
      return this.registerFailure(change);
    }

    if (change.type === 'Restore') {
      return this.verifyRestoreContact(change, channel);
    }

    const completed: ProviderContactChange = {
      ...change,
      status: 'Completed',
      codeExpiresAtUtc: null,
      attemptsRemaining: 0,
      resendCooldownSeconds: 0,
      resendsRemaining: 0,
    };
    this.changes.set(id, completed);
    return of(completed).pipe(delay(300));
  }

  resend(
    _patientId: string,
    id: string,
    channel?: ProviderContactChangeChannel
  ): Observable<ProviderContactChange> {
    const change = this.changes.get(id);
    if (!change) {
      return this.fail(404, 'CONTACT_CHANGE_NOT_FOUND');
    }

    if (change.resendsRemaining === 0) {
      this.changes.set(id, { ...change, status: 'Cancelled' });
      return this.fail(409, 'CONTACT_CHANGE_REQUEST_LIMIT_REACHED');
    }

    if (
      change.type === 'Restore' &&
      change.status === 'PendingNewVerification'
    ) {
      const key = channel === 'Phone' ? 'phone' : 'email';
      const contact = change.restore?.[key];
      const resentContact: ProviderContactChange = {
        ...change,
        resendsRemaining: change.resendsRemaining - 1,
        restore: {
          identityMethod: change.restore?.identityMethod ?? null,
          email: change.restore?.email ?? null,
          phone: change.restore?.phone ?? null,
          [key]: contact ? this.sentContact(contact.maskedValue) : null,
        },
      };
      this.changes.set(id, resentContact);
      return of(resentContact).pipe(delay(300));
    }

    const resent: ProviderContactChange = {
      ...change,
      resendCooldownSeconds: 30,
      resendsRemaining: change.resendsRemaining - 1,
      codeExpiresAtUtc: new Date(Date.now() + CODE_LIFETIME_MS).toISOString(),
    };
    this.changes.set(id, resent);
    return of(resent).pipe(delay(300));
  }

  cancel(_patientId: string, id: string): Observable<ProviderContactChange> {
    const change = this.changes.get(id);
    if (!change) {
      return this.fail(404, 'CONTACT_CHANGE_NOT_FOUND');
    }

    const cancelled: ProviderContactChange = { ...change, status: 'Cancelled' };
    this.changes.set(id, cancelled);
    return of(cancelled);
  }

  private createRestore(
    request: Extract<CreateProviderContactChangeRequest, { type: 'Restore' }>
  ): Observable<ProviderContactChange> {
    if (!request.identityMethod || !request.identityConfirmed) {
      return this.fail(400, 'CONTACT_CHANGE_IDENTITY_CONFIRMATION_REQUIRED');
    }

    const now = Date.now();
    const change: ProviderContactChange = {
      id: crypto.randomUUID(),
      type: 'Restore',
      status: 'PendingCurrentVerification',
      maskedApprovalDestination: '+1 **** *** 8888',
      maskedNewValue: '',
      expiresAtUtc: new Date(now + 30 * 60 * 1000).toISOString(),
      codeExpiresAtUtc: new Date(now + CODE_LIFETIME_MS).toISOString(),
      attemptsRemaining: 5,
      resendCooldownSeconds: 30,
      resendsRemaining: 5,
      restore: {
        identityMethod: request.identityMethod,
        email: null,
        phone: null,
      },
    };
    this.changes.set(change.id, change);
    return of(change).pipe(delay(300));
  }

  private verifyRestoreContact(
    change: ProviderContactChange,
    channel: ProviderContactChangeChannel | undefined
  ): Observable<ProviderContactChange> {
    const key = channel === 'Phone' ? 'phone' : 'email';
    const contact = change.restore?.[key];
    if (!channel || !contact) {
      return this.fail(409, 'CONTACT_CHANGE_INVALID_STATE');
    }

    const restore = {
      identityMethod: change.restore?.identityMethod ?? null,
      email: change.restore?.email ?? null,
      phone: change.restore?.phone ?? null,
      [key]: {
        ...contact,
        verified: true,
        codeSent: false,
        codeExpiresAtUtc: null,
        resendCooldownSeconds: 0,
      },
    };
    const completed = !!restore.email?.verified && !!restore.phone?.verified;
    const updated: ProviderContactChange = {
      ...change,
      status: completed ? 'Completed' : change.status,
      attemptsRemaining: completed ? 0 : change.attemptsRemaining,
      resendsRemaining: completed ? 0 : change.resendsRemaining,
      restore,
    };
    this.changes.set(change.id, updated);
    return of(updated).pipe(delay(300));
  }

  private sentContact(maskedValue: string): ProviderAccessRestoreContactState {
    return {
      maskedValue,
      verified: false,
      codeSent: true,
      codeExpiresAtUtc: new Date(Date.now() + CODE_LIFETIME_MS).toISOString(),
      resendCooldownSeconds: 30,
    };
  }

  private registerFailure(
    change: ProviderContactChange
  ): Observable<ProviderContactChange> {
    const attemptsRemaining = Math.max(0, change.attemptsRemaining - 1);
    if (attemptsRemaining === 0) {
      this.changes.set(change.id, {
        ...change,
        status: 'Cancelled',
        attemptsRemaining,
      });
      return this.fail(409, 'CONTACT_CHANGE_REQUEST_LIMIT_REACHED');
    }

    const updated = { ...change, attemptsRemaining };
    this.changes.set(change.id, updated);
    return this.fail(400, 'CONTACT_CHANGE_INVALID_CODE', updated);
  }

  private mask(type: 'Email' | 'Phone', value: string): string {
    if (type === 'Phone') {
      return `+1 **** *** ${value.slice(-4)}`;
    }

    const [local, domain] = value.split('@');
    return `${local?.[0] ?? ''}****@${domain ?? ''}`;
  }

  private fail(
    status: number,
    code: string,
    details?: unknown
  ): Observable<never> {
    return throwError(
      () =>
        new HttpErrorResponse({
          status,
          error: { code, message: code, details },
        })
    );
  }
}
